type DateLike = Date | string | number | null;

export const getDateInEuropeBerlin = (date: DateLike = null): Date => {
    const baseDate = date ? new Date(date) : new Date();
    const formatter = new Intl.DateTimeFormat('de-DE', {
        timeZone: 'Europe/Berlin',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });

    const parts = formatter.formatToParts(baseDate);
    const year = parts.find((p) => p.type === 'year')?.value;
    const month = parts.find((p) => p.type === 'month')?.value;
    const day = parts.find((p) => p.type === 'day')?.value;

    if (!year || !month || !day) {
        return new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
    }

    return new Date(Number(year), Number(month) - 1, Number(day));
};

/**
 * Returns the date in Europe/Berlin timezone as YYYY-MM-DD.
 *
 * @param date - The date to format
 * @returns The date in Europe/Berlin timezone as YYYY-MM-DD
 */
export const formatDateInEuropeBerlin = (date: DateLike = null): `${string}-${string}-${string}` => {
    const berlinDate = getDateInEuropeBerlin(date);
    const y = berlinDate.getFullYear();
    const m = String(berlinDate.getMonth() + 1).padStart(2, '0');
    const d = String(berlinDate.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

export const getDateTimeInEuropeBerlin = (date: DateLike = null): Date => {
    const baseDate = date ? new Date(date) : new Date();
    const formatter = new Intl.DateTimeFormat('de-DE', {
        timeZone: 'Europe/Berlin',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });

    const parts = formatter.formatToParts(baseDate);
    const year = parts.find((p) => p.type === 'year')?.value;
    const month = parts.find((p) => p.type === 'month')?.value;
    const day = parts.find((p) => p.type === 'day')?.value;
    const hour = parts.find((p) => p.type === 'hour')?.value;
    const minute = parts.find((p) => p.type === 'minute')?.value;

    if (!year || !month || !day || hour === undefined || minute === undefined) {
        return baseDate;
    }

    return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute));
};

/** Compares calendar date only (year, month, day); time of day is ignored. */
export const isSameCalendarDay = (a?: Date, b?: Date): boolean => {
    if (!a || !b) return false;

    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

/**
 * Parses a Storyblok datetime field (format `YYYY-MM-DD HH:mm`, stored in UTC) into a `Date`.
 *
 * `new Date('2026-01-01 00:00')` would otherwise interpret the string as local time, producing
 * different instants depending on the runtime timezone (e.g. server vs. client).
 *
 * @param input - The Storyblok datetime string
 * @returns The parsed `Date`, or `null` if the input is missing or malformed
 */
export const parseStoryblokDateUTC = (input: string): Date | null => {
    const match = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(input.trim());
    if (!match) return null;

    const [, year, month, day, hour, minute] = match;
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute)));
    return Number.isNaN(date.getTime()) ? null : date;
};
